import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { DEFAULT_PO_SETTINGS, fmtMoney } from '../lib/poCore'
import { poStyles, errText, n } from '../lib/poUi'
import { ApprovalEmailToggle } from '../components/ApprovalsCard'
import { actorName } from '../lib/fleetCore'

// Settings -> Purchase Orders: approval limits and exchange rates per vessel. Only the Captain or management can change them (database policy).
export default function POSettings({ config, phone, roles, settings, onSaved }) {
  const { card, lab, inp, fl } = poStyles(phone)
  const canSet = (roles || []).includes('captain') || (roles || []).includes('management')
  const toForm = s => { const v = { ...DEFAULT_PO_SETTINGS, ...(s || {}) }; return { currency: v.currency, tier1_max: String(v.tier1_max), tier2_max: String(v.tier2_max), rates: Object.entries(v.rates || {}).map(([code, rate]) => ({ code, rate: String(rate) })) } }
  const [f, setF] = useState(toForm(settings))
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [ok, setOk] = useState('')
  useEffect(() => { setF(toForm(settings)) }, [settings]) // eslint-disable-line

  async function save() {
    const t1 = n(f.tier1_max), t2 = n(f.tier2_max), cur = String(f.currency || '').trim().toUpperCase()
    if (!/^[A-Z]{3}$/.test(cur)) return setErr('Currency: 3 letters, e.g. EUR')
    if (!(t1 >= 0) || !(t2 >= t1)) return setErr('The second limit must be at least the first one.')
    const rates = {}
    for (const r of f.rates) {
      const c = String(r.code || '').trim().toUpperCase(), v = n(r.rate)
      if (!c && !r.rate) continue
      if (!/^[A-Z]{3}$/.test(c) || !(v > 0)) return setErr('Each rate needs a 3-letter currency and a number above 0.')
      if (c !== cur) rates[c] = v
    }
    setErr(''); setBusy(true)
    const { error } = await supabase.from('po_settings').upsert({ client_id: config.id, currency: cur, tier1_max: t1, tier2_max: t2, rates, updated_by_name: actorName(config) }, { onConflict: 'client_id' })
    setBusy(false)
    if (error) return setErr(errText(error))
    setOk('Saved. Draft orders use the new limits from their next change or approval.'); setTimeout(() => setOk(''), 6000)
    onSaved && onSaved()
  }

  const cur = f.currency || 'EUR'
  const rule = [
    ['Up to ' + fmtMoney(n(f.tier1_max) || 0, cur), '1 approval: anyone on board with Full on Purchase Orders (head of department, chief, captain)'],
    [fmtMoney(n(f.tier1_max) || 0, cur) + ' – ' + fmtMoney(n(f.tier2_max) || 0, cur), '1 approval: the Captain or management'],
    ['Over ' + fmtMoney(n(f.tier2_max) || 0, cur), '2 approvals: the Captain AND management'],
    ['Items without a price', 'At least the Captain'],
    ['Currency without a rate below', 'The Captain AND management'],
  ]
  return (
    <>
      <div style={card}>
        <div style={lab}>Who approves an order</div>
        <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {rule.map(([a, b]) => <div key={a} style={{ display: 'flex', gap: 10, fontSize: 13, flexWrap: phone ? 'wrap' : 'nowrap' }}><strong style={{ minWidth: phone ? '100%' : 220 }}>{a}</strong><span style={{ color: 'var(--muted)' }}>{b}</span></div>)}
        </div>
        <div style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 10 }}>Captain = the vessel account, or a crew login with role Captain and Full on Purchase Orders. Management = the company login of this vessel{config?.company_id ? '' : ' - this vessel has no management company, so the Captain alone approves where management is needed'}. Changing supplier, port, date, currency or items clears approvals already given; notes and photos do not.</div>
      </div>
      <div style={card}>
        <div style={{ ...lab, marginBottom: 8 }}>Limits</div>
        <div style={{ display: 'grid', gridTemplateColumns: phone ? '1fr 1fr' : 'repeat(3, minmax(0, 200px))', gap: 10 }}>
          <label style={fl}>Currency<input style={inp} disabled={!canSet} maxLength={3} value={f.currency} onChange={e => setF(x => ({ ...x, currency: e.target.value.toUpperCase() }))} /></label>
          <label style={fl}>First limit<input style={inp} disabled={!canSet} inputMode="decimal" value={f.tier1_max} onChange={e => setF(x => ({ ...x, tier1_max: e.target.value }))} /></label>
          <label style={fl}>Second limit<input style={inp} disabled={!canSet} inputMode="decimal" value={f.tier2_max} onChange={e => setF(x => ({ ...x, tier2_max: e.target.value }))} /></label>
        </div>
        <div style={{ ...lab, margin: '14px 0 6px' }}>Exchange rates (1 unit = x {cur})</div>
        {f.rates.map((r, k) => (
          <div key={k} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
            <input style={{ ...inp, width: 80 }} disabled={!canSet} maxLength={3} placeholder="USD" value={r.code} onChange={e => { const v = e.target.value.toUpperCase(); setF(x => ({ ...x, rates: x.rates.map((y, j) => j === k ? { ...y, code: v } : y) })) }} />
            <span style={{ fontSize: 13, color: 'var(--muted)' }}>=</span>
            <input style={{ ...inp, width: 110 }} disabled={!canSet} inputMode="decimal" placeholder="0.92" value={r.rate} onChange={e => { const v = e.target.value; setF(x => ({ ...x, rates: x.rates.map((y, j) => j === k ? { ...y, rate: v } : y) })) }} />
            <span style={{ fontSize: 13, color: 'var(--muted)' }}>{cur}</span>
            {canSet && <button className="act-btn del" onClick={() => setF(x => ({ ...x, rates: x.rates.filter((_, j) => j !== k) }))}>×</button>}
          </div>
        ))}
        {canSet && <button className="btn" onClick={() => setF(x => ({ ...x, rates: [...x.rates, { code: '', rate: '' }] }))}>+ Rate</button>}
        {err && <div style={{ color: '#C93B47', fontSize: 13, marginTop: 8 }}>{err}</div>}
        {ok && <div style={{ color: '#137050', fontSize: 13, marginTop: 8 }}>{ok}</div>}
        {canSet ? <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}><button className="btn btn-primary" disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save settings'}</button></div>
          : <div style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 10 }}>Only the Captain or management can change these.</div>}
        {settings?.updated_by_name && <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 8 }}>Last changed by {settings.updated_by_name}</div>}
      </div>
      <div style={card}>
        <div style={lab}>My notifications</div>
        <div style={{ fontSize: 13, color: 'var(--muted)', margin: '6px 0 10px' }}>For this login only. As Captain or management you get an email when an order or an APA close waits for your approval, and one reminder a day for anything waiting more than 24 hours. Approved / rejected updates always show in the app.</div>
        <ApprovalEmailToggle />
      </div>
    </>
  )
}
